import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";

import { useMutationWithError } from "@/utils/hooks/useMutationWithError";
import { useFlags } from "@/utils/launchDarkly/useFlags";
import { useToast } from "@whoosh/design-system/src/components/overlay/Toast/useToast";

import {
  RemovePaymentRaincheckFromBookingInput,
  useRemovePaymentRaincheckFromBookingMutation,
} from "./__generated__/useRemovePaymentRaincheckFromBookingMutation.graphql";

export const useRemovePaymentRaincheckFromBooking = () => {
  const { showToast } = useToast();
  const { patronLoyalty } = useFlags();
  const [
    removePaymentRaincheckFromBooking,
    isRemovingPaymentRaincheckFromBooking,
  ] = useMutationWithError<useRemovePaymentRaincheckFromBookingMutation>(
    useRemovePaymentRaincheckFromBookingMutationString
  );

  const handleResult = (success: boolean | null | undefined) => {
    if (success) {
      showToast({
        description: t`Raincheck removed from booking.`,
        semanticStyle: "success",
        toastId: "remove-payment-raincheck-from-booking-success",
      });
    } else {
      showToast({
        description: t`Sorry. An error occurred while removing a raincheck from the booking.`,
        semanticStyle: "error",
        toastId: "remove-payment-raincheck-from-booking-error",
      });
    }
  };

  const handleRemovePaymentRaincheckFromBooking = (
    input: RemovePaymentRaincheckFromBookingInput
  ) => {
    removePaymentRaincheckFromBooking({
      variables: { input, patronLoyalty: !!patronLoyalty },
      onCompleted: (response) =>
        handleResult(response.responsePayload?.success),
    });
  };

  return [
    handleRemovePaymentRaincheckFromBooking,
    isRemovingPaymentRaincheckFromBooking,
  ] as const;
};

export const useRemovePaymentRaincheckFromBookingMutationString = graphql`
  mutation useRemovePaymentRaincheckFromBookingMutation(
    $input: RemovePaymentRaincheckFromBookingInput!
    $patronLoyalty: Boolean!
  ) {
    responsePayload: removePaymentRaincheckFromBooking(input: $input) {
      success
      errors {
        field
        code
        message
      }
      booking {
        id
        totalDueAfterCredits
        projectedCreditApplications @include(if: $patronLoyalty) {
          amount
          paymentSource
          squareGiftCard {
            id
            ganLastFour
          }
          raincheck {
            id
          }
        }
        ... on StandardBooking {
          paymentRainchecks {
            edges {
              node {
                id
              }
            }
          }
        }
        ... on LessonBooking {
          paymentRainchecks {
            edges {
              node {
                id
              }
            }
          }
        }
      }
    }
  }
`;
